import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import EnterpriseMembersContext from './EnterpriseMembers.context';
import useQueryParams from 'hooks/useQueryParams';
import useSelect from 'hooks/useSelect';
import {
  Enterprise,
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  EnterpriseBusinessUnit,
  EnterpriseMember,
  EnterpriseMemberInvitationStatus,
} from 'models/Enterprises';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import PencilIcon from 'icons/Pencil.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import ActionMenu from 'components/ActionMenu';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useLocation } from 'react-router-dom';
import api from 'api';
import { ManageMemberDrawer } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/subrouters/Enterprises/subrouters/Enterprise/pages/ManagePeople/components/ManageMemberDrawer';
import BellIcon from 'icons/Bell.icon';
import { getUserFullName } from 'utils';
import useRefetch from 'hooks/useRefetch';
import useUndoActions from 'hooks/useUndoActions';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useEnterpriseBrokers from 'hooks/useEnterpriseBrokers';
import useBusinessUnits from 'hooks/useBusinessUnits';
import useMemberSeniorities from 'hooks/useMemberSeniorities';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';

const DEFAULT_SORT = '-invitationStatusSort';

export const checkIsUserNotInvited = (
  invitationStatus: EnterpriseMemberInvitationStatus,
) => invitationStatus === ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED;

export const checkUserHasInvitationSent = (
  invitationStatus: EnterpriseMemberInvitationStatus,
) => invitationStatus === ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT;

const checkIfEveryUserIsNotInvited = (members: EnterpriseMember[]) =>
  members.every(({ invitationStatus }) =>
    checkIsUserNotInvited(invitationStatus),
  );

const checkIfEveryUserHasInvitationSent = (members: EnterpriseMember[]) =>
  members.every(({ invitationStatus }) =>
    checkUserHasInvitationSent(invitationStatus),
  );

const getName = (member?: EnterpriseMember) =>
  getUserFullName(member?.firstName, member?.lastName) || member?.email;

type EnterpriseMembersProps = {
  enterprise: Partial<Enterprise>;
  refetchFlag?: boolean;
  updateBusinessUnits: (
    updatedBusinessUnits: EnterpriseBusinessUnit[],
    shouldRefetch?: boolean,
  ) => void;
};

const EnterpriseMembers: React.FC<EnterpriseMembersProps> = (props) => {
  const { children, enterprise, refetchFlag: enterpriseRefetchFlag } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<EnterpriseMember[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  const [isLoadingAddMember, setIsLoadingAddMember] = useState(false);
  const [isLoadingRemoveMembers, setIsLoadingRemoveMembers] = useState(false);
  const [isLoadingUndoRemoveMembers, setIsLoadingUndoRemoveMembers] =
    useState(false);

  const [removeEnterpriseMembers, setRemoveEnterpriseMembers] = useState<
    EnterpriseMember[]
  >([]);

  const [selectedMember, setSelectedMember] = useState<EnterpriseMember>();
  const [managePeopleDrawer, managePeopleDrawerRef] =
    useCallbackRef<SideDrawerRef | null>(null);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { t } = useTranslation();
  const pathname = useLocation();

  const enterpriseId = enterprise?._id;

  const {
    params: {
      sort,
      page,
      invitationStatus,
      seniority,
      enterpriseBusinessUnitId,
    },
    setQueryParam,
  } = useQueryParams();

  const {
    selected,
    isAllSelected,
    isSingleSelected,
    isSelectedMode,
    selectAll,
    removeAll,
    updateSelected,
    updateSelectedValue,
  } = useSelect<EnterpriseMember>(items);

  const notInvitedMembers = selected.filter(({ invitationStatus }) =>
    checkIsUserNotInvited(invitationStatus),
  );

  const hasNotInvitedMembers = !!notInvitedMembers.length;

  const isEmpty = !isLoading && !items?.length;

  const { refetchFlag, refetch } = useRefetch();

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const isRemoveDisabled =
    isUndoAvailable || isLoadingRemoveMembers || isLoadingUndoRemoveMembers;

  const onUndoDeleteRequest = useCallback(
    async () =>
      removeEnterpriseMembers?.length === 1
        ? api.enterprises.undoDeleteMember(removeEnterpriseMembers[0]?._id)
        : api.enterprises.undoBulkDeleteMembers(enterprise?._id),
    [enterprise?._id, removeEnterpriseMembers],
  );

  const onUndo = useCallback(async () => {
    try {
      setIsLoadingUndoRemoveMembers(true);
      clearUndoTimeout();
      await onUndoDeleteRequest();
      refetch();
      setRemoveEnterpriseMembers([]);
    } catch {
      return;
    } finally {
      setIsLoadingUndoRemoveMembers(false);
    }
  }, [clearUndoTimeout, onUndoDeleteRequest, refetch]);

  const deleteMembers = useCallback(
    async (members: EnterpriseMember[]) => {
      try {
        clearUndoTimeout();
        setRemoveEnterpriseMembers(members);
        setIsLoadingRemoveMembers(true);

        const membersIds = members.map((el) => el._id);

        membersIds?.length === 1
          ? await api.enterprises.deleteMember(membersIds[0])
          : await api.enterprises.bulkDeleteMembers(
              enterprise?._id,
              membersIds,
            );

        refetch();
        removeAll();
        setUndoTimeout(() => setRemoveEnterpriseMembers([]));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingRemoveMembers(false);
      }
    },
    [
      enterprise?._id,
      clearUndoTimeout,
      refetch,
      removeAll,
      setUndoTimeout,
      popServerError,
    ],
  );

  const handleAddPeople = useCallback(async () => {
    try {
      setIsLoadingAddMember(true);

      const { data } = await api.enterprises.createInitialMember(enterpriseId);
      setSelectedMember(data);
      managePeopleDrawer?.setOpen(true);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoadingAddMember(false);
    }
  }, [managePeopleDrawer, enterpriseId, popServerError]);

  const handleEditMember = useCallback(
    (member: EnterpriseMember) => {
      setSelectedMember(member);
      managePeopleDrawer?.setOpen(true);
    },
    [managePeopleDrawer],
  );

  const handleUpdateMember = useCallback(
    (member: EnterpriseMember) => {
      const isEdit = items.find((el) => el._id === member._id);

      if (isEdit) {
        setItems((old) => {
          updateSelectedValue(member);
          return old.map((el) => (el._id === member._id ? member : el));
        });
      } else refetch();

      setSelectedMember(null);
    },
    [items, refetch, updateSelectedValue],
  );

  const handleSendInvites = useCallback(
    async (members: EnterpriseMember[]) => {
      const count = members?.length;

      if (
        await confirm(
          {
            title: t('sendInvites', { count }),
            content: t('areYouSureYouWantToSendInvites', {
              count,
              member: getName(members[0]),
            }),
          },
          true,
        )
      ) {
        members.map((el) =>
          handleUpdateMember({
            ...el,
            lastInviteSent: new Date(),
            invitationStatus:
              ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT,
          }),
        );
        try {
          await api.enterprises.sendInvites(
            enterpriseId,
            members.map((el) => el._id),
          );
        } catch (e) {
          popServerError(e);
          members.map((el) =>
            handleUpdateMember({
              ...el,
              invitationStatus: ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED,
            }),
          );
        }
      }
    },
    [enterpriseId, handleUpdateMember, popServerError, t],
  );

  const handleSendReminders = useCallback(
    async (members: EnterpriseMember[]) => {
      const count = members?.length;

      if (
        await confirm(
          {
            title: t('sendReminders', { count }),
            content: t('areYouSureYouWantToSendReminders', {
              count,
              member: getName(members[0]),
            }),
          },
          true,
        )
      ) {
        try {
          members.map((el) =>
            handleUpdateMember({
              ...el,
              lastInviteSent: new Date(),
            }),
          );
          await api.enterprises.sendInvites(
            enterpriseId,
            members.map((el) => el._id),
          );
          popSuccess(t('sendRemindersSuccess', { count }));
        } catch (e) {
          popServerError(e);
        }
      }
    },
    [enterpriseId, popSuccess, handleUpdateMember, popServerError, t],
  );

  const handleSendInvitesToNonInvited = useCallback(
    () => handleSendInvites(notInvitedMembers),
    [notInvitedMembers, handleSendInvites],
  );

  const checkIfMemberIsSelected = useCallback(
    (member: EnterpriseMember) =>
      !!selected.find(
        ({ _id }) => _id === (member as unknown as EnterpriseMember)._id,
      ),
    [selected],
  );

  const onSelectAll = useCallback(() => {
    if (isAllSelected) {
      removeAll();
    } else {
      selectAll();
    }
  }, [isAllSelected, removeAll, selectAll]);

  const actions = useMemo(
    () => [
      ...(isSingleSelected
        ? [
            {
              icon: PencilIcon,
              disabled: isRemoveDisabled,
              onClick: () => handleEditMember(selected[0]),
            },
          ]
        : []),
      ...(checkIfEveryUserHasInvitationSent(selected)
        ? [
            {
              icon: BellIcon,
              disabled: isRemoveDisabled,
              onClick: () => handleSendReminders(selected),
            },
          ]
        : []),
      ...(checkIfEveryUserIsNotInvited(selected)
        ? [
            {
              icon: ArrowrightIcon,
              disabled: isRemoveDisabled,
              onClick: () => handleSendInvites(selected),
            },
          ]
        : []),
      ...(!isUndoAvailable
        ? [
            {
              alert: true,
              disabled: isRemoveDisabled,
              icon: TrashIcon,
              onClick: () => deleteMembers(selected),
            },
          ]
        : []),
    ],
    [
      selected,
      isSingleSelected,
      isUndoAvailable,
      isRemoveDisabled,
      deleteMembers,
      handleEditMember,
      handleSendInvites,
      handleSendReminders,
    ],
  );

  const hasActionMenu = isSelectedMode && !!actions.length;

  const getDropdownOptions = useCallback(
    (members: EnterpriseMember[]) => [
      ...(members?.length === 1
        ? [
            {
              label: t('edit'),
              icon: <PencilIcon />,
              disabled: isRemoveDisabled,
              onClick: () => handleEditMember(members[0]),
            },
          ]
        : []),
      ...(checkIfEveryUserHasInvitationSent(members)
        ? [
            {
              label: t('sendReminders', { count: members?.length }),
              icon: <BellIcon />,
              disabled: isRemoveDisabled,
              onClick: () => handleSendReminders(members),
            },
          ]
        : []),
      ...(checkIfEveryUserIsNotInvited(members)
        ? [
            {
              label: t('sendInvites', { count: members?.length }),
              icon: <ArrowrightIcon />,
              disabled: isRemoveDisabled,
              onClick: () => handleSendInvites(members),
            },
          ]
        : []),
      ...(!isUndoAvailable
        ? [
            {
              label: t('remove'),
              alert: true,
              disabled: isRemoveDisabled,
              icon: <TrashIcon />,
              onClick: () => deleteMembers(members),
            },
          ]
        : []),
    ],
    [
      isUndoAvailable,
      isRemoveDisabled,
      t,
      handleEditMember,
      handleSendReminders,
      handleSendInvites,
      deleteMembers,
    ],
  );

  const brokers = useEnterpriseBrokers(enterprise);
  const { businessUnitsOptions } = useBusinessUnits(enterprise?.businessUnits);
  const { memberSenioritiesOptions } = useMemberSeniorities(
    enterprise?.memberSeniorities,
  );

  useEffect(() => {
    if (!sort) {
      setQueryParam('sort', DEFAULT_SORT);
    }
  }, [sort, setQueryParam]);

  useEffect(() => {
    removeAll();
  }, [pathname, page, sort, refetchFlag, enterpriseRefetchFlag, removeAll]);

  useEffect(() => {
    if (!sort || !enterpriseId) return;

    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.enterprises.getEnterprisePeople(enterpriseId, {
          $limit: limit,
          $page: page,
          $sort: sort,
          invitationStatus,
          seniority,
          enterpriseBusinessUnitId,
        });
        setItems(items);
        setTotalPages(totalPages);
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    limit,
    refetchFlag,
    enterpriseRefetchFlag,
    enterpriseId,
    sort,
    page,
    seniority,
    invitationStatus,
    enterpriseBusinessUnitId,
  ]);

  return (
    <EnterpriseMembersContext.Provider
      value={{
        enterpriseId: enterprise?._id,
        members: items,
        isLoading,
        isEmpty,
        totalPages,
        hasNotInvitedMembers,
        isLoadingAddMember,
        isAllSelected,
        selected,
        isRemoveDisabled,
        brokers,
        limit,
        units: businessUnitsOptions,
        memberSeniorities: memberSenioritiesOptions,
        getDropdownOptions,
        updateSelected,
        onSelectAll,
        handleEditMember,
        refetch,
        handleAddPeople,
        handleSendReminders,
        handleSendInvites,
        handleSendInvitesToNonInvited,
        checkIfMemberIsSelected,
        deleteMembers,
        updateLimit,
      }}
    >
      {hasActionMenu && (
        <ActionMenu
          actions={actions}
          onClose={removeAll}
          label={t('selectedMembers', { count: selected.length })}
        />
      )}

      {children}

      <ManageMemberDrawer
        member={selectedMember}
        enterprise={enterprise}
        ref={managePeopleDrawerRef}
        onSuccess={handleUpdateMember}
        onClose={() => setSelectedMember(null)}
      />

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingUndoRemoveMembers}
        onUndo={onUndo}
      >
        <p>
          {t('memberRemovedFromEnterprise', {
            count: removeEnterpriseMembers.length,
            brandName: enterprise?.brandName,
            memberName: getUserFullName(
              removeEnterpriseMembers[0]?.firstName,
              removeEnterpriseMembers[0]?.lastName,
            ),
          })}
        </p>
      </PopNotificationWithUndo>
    </EnterpriseMembersContext.Provider>
  );
};

export default EnterpriseMembers;
